import React, {FC, useEffect, useMemo, useState} from 'react';
import Box from '@material-ui/core/Box';
import {Filter} from '@epic-ui/icons';
import {getLocale} from '@epic-core/common/dist';
import {useTheme} from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import Trend from '../../../../trend/Trend';
import {Widget} from '../../../../common/widget';
import LineChart from '../components/chart/LineChart';
import WidgetTooltip from '../components/WidgetTooltip';
import HorizontalLabel from '../components/HorizontalLabel';
import SummaryItemLabel from '../components/SummaryItemLabel';
import IntervalRadioGroup from '../components/interval-radio-group/IntervalRadioGroup';
import ChartOverview from '../../../../chart-overview/ChartOverview';
import WidgetGameSelect from '../../../../game-select/WidgetGameSelect';
import WidgetDatePicker from '../../../../widget-date-picker/WidgetDatePicker';
import WidgetDownloadButton from '../components/WidgetDownloadButton';
import TimeScaleSelect from '../../../../time-scale-select/TimeScaleSelect';
import IconButton from '../../../../common/IconButton';

import useChartTrends from '../hooks/useChartTrends';
import useActiveUsersData from './useActiveUsersData';
import {prepareLineChartExportData} from '../exportUtils';
import {dateTimeFormatter, getWidgetTimeRangeDates} from '../utils';
import {useRangePickerData} from '../../../../../components/common/date-range-picker';
import {xAxisFormatters, LEGENDS_LIMIT, tooltipFormatters} from '../constants';
import {TIME_SCALES} from '../../../../constants';
import {numberFormatter} from '../../../../../utils/FNUtils';

import {WidgetProps} from '../types';
import {Status, DateRange, TimeScale} from '../../../../../types';

import {ChartWrapper, OptionWrapper} from '../styles';
import {PDFPictureTrap} from '../export/PDFExportProvider';
import useLocalization from '../../../../../hooks/useLocalization';
import useWidgetSelectedGames from '../hooks/useWidgetSelectedGames';
import useActiveUsersOverviewData from './useActiveUsersOverviewData';

const CODES = {
    title: 'epic.affiliate.fortnite_games.active_users',
    tooltip: 'epic.affiliate.fortnite_games.active_users.tooltip',
    peakAllTime: 'epic.affiliate.fortnite_games.active_users.peak_all_time',
    peakToday: 'epic.affiliate.fortnite_games.active_users.peak_today',
    avgAllTime: 'epic.affiliate.fortnite_games.active_users.avg_all_time',
    avgToday: 'epic.affiliate.fortnite_games.active_users.avg_today',
    timeScale: 'epic.affiliate.fortnite_games.time_scale.',
    showMe: 'epic.affiliate.fortnite_games.show_me',
    tooltipPeakAllTime: 'epic.affiliate.fortnite_games.active_users.overview.peak_all_time',
    tooltipPeakToday: 'epic.affiliate.fortnite_games.active_users.overview.peak_today',
    tooltipAvgAllTime: 'epic.affiliate.fortnite_games.active_users.overview.avg_all_time',
    tooltipAvgToday: 'epic.affiliate.fortnite_games.active_users.overview.avg_today'
};

const TotalActiveUsersWidget: FC<WidgetProps> = ({
    globalTimeScale,
    selectedDate: selected,
    startDate: start,
    endDate: end,
    selectedGames: selectedGamesProps,
    gamesList,
    keysMap,
    gamesLoading
}: WidgetProps) => {
    const getMessage = useLocalization();

    const theme = useTheme();
    const isFmdBreakpoint = useMediaQuery(theme.breakpoints.up('fmd'));
    const isXlBreakpoint = useMediaQuery(theme.breakpoints.up('xl'));

    const timeScaleOptions = useMemo(() => {
        return TIME_SCALES.map(value => ({
            label: getMessage(`${CODES.timeScale}${value}`),
            value
        }));
    }, []);

    const [timeScale, setTimeScale] = useState(timeScaleOptions[1]);
    const [isPreselectedRange, setIsPreselectedRange] = useState(true);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [selectedGames, handleSelectGames] = useWidgetSelectedGames(selectedGamesProps);

    const {selectedDate, startDate, endDate, setCalendar} = useRangePickerData(
        getWidgetTimeRangeDates(TimeScale.DAILY)
    );

    const {data, loading, error, reload} = useActiveUsersData({
        startDate,
        endDate,
        timeScale: timeScale.value,
        selectedGames
    });

    const {peak, avg} = useActiveUsersOverviewData({
        selectedGames
    });

    const formatNumber = numberFormatter(getLocale());

    const chartData = data[timeScale.value].list || [];
    const emptyDataKeys = data[timeScale.value].empty;

    const trends = useChartTrends({data: chartData, xAxis: 'time'});

    // Handlers
    const handleRangeChange = (dateRange: DateRange) => {
        setCalendar(dateRange);
        setIsPreselectedRange(false);
    };

    const handleTimeScaleChange = timeScale => {
        const timeRangeDates = getWidgetTimeRangeDates(timeScale.value);

        setTimeScale(timeScale);
        setCalendar(timeRangeDates);
        setIsPreselectedRange(false);
    };

    const handleMobileMenuClick = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    // Effects
    useEffect(() => {
        const selectedOption = timeScaleOptions.find(({value}) => value === globalTimeScale);
        if (!isPreselectedRange) {
            setTimeScale(selectedOption);
        }

        if (!isPreselectedRange || (!isMobileMenuOpen && !isFmdBreakpoint)) {
            setCalendar({startDate: start, endDate: end, selectedDate: selected});
        }
    }, [start, end, selected]);

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [isFmdBreakpoint]);

    const GameSelect = (
        <WidgetGameSelect options={gamesList} value={selectedGames} onChange={handleSelectGames} />
    );

    const TimeSelect = (
        <TimeScaleSelect
            options={timeScaleOptions}
            value={timeScale}
            onChange={handleTimeScaleChange}
        />
    );

    const DatePicker = (
        <WidgetDatePicker
            timeScale={timeScale.value}
            selectedDate={selectedDate}
            startDate={startDate}
            endDate={endDate}
            onRangeChange={handleRangeChange}
        />
    );

    return (
        <PDFPictureTrap id={CODES.title} index={1}>
            <div>
                <Widget
                    height={647}
                    header={getMessage(CODES.title)}
                    padding="15px"
                    tooltip={<WidgetTooltip>{getMessage(CODES.tooltip)}</WidgetTooltip>}
                    status={error ? Status.ERROR : undefined}
                    message={getMessage(error) || error}
                    overflow="visible"
                    retry={reload}
                    actions={
                        <>
                            {isFmdBreakpoint ? (
                                <>
                                    {isXlBreakpoint ? (
                                        <HorizontalLabel>
                                            {getMessage(CODES.showMe)}
                                            <IntervalRadioGroup
                                                options={timeScaleOptions}
                                                value={timeScale}
                                                onChange={handleTimeScaleChange}
                                            />
                                        </HorizontalLabel>
                                    ) : (
                                        <Box mx={1.5} width={140}>
                                            {TimeSelect}
                                        </Box>
                                    )}
                                    <Box mx={1.5} width={233}>
                                        {DatePicker}
                                    </Box>
                                    <Box mx={1.5} width={178}>
                                        {GameSelect}
                                    </Box>
                                </>
                            ) : (
                                <IconButton
                                    onClick={handleMobileMenuClick}
                                    isOpen={isMobileMenuOpen}>
                                    <Filter />
                                </IconButton>
                            )}
                            <WidgetDownloadButton
                                areaId={CODES.title}
                                fileName={getMessage(CODES.title)}
                                data={chartData}
                                prepareCSVData={prepareLineChartExportData(
                                    keysMap,
                                    timeScale.value === TimeScale.LIVE
                                        ? dateTimeFormatter
                                        : undefined
                                )}
                                includeTime={timeScale.value === TimeScale.LIVE}
                                disabled={loading || gamesLoading || !!error}
                            />
                        </>
                    }
                    options={
                        isMobileMenuOpen &&
                        !isFmdBreakpoint && (
                            <OptionWrapper>
                                {TimeSelect}
                                {DatePicker}
                                {GameSelect}
                            </OptionWrapper>
                        )
                    }>
                    <ChartWrapper>
                        <LineChart
                            selectedGames={selectedGames}
                            height={490}
                            data={chartData}
                            trends={trends}
                            limit={LEGENDS_LIMIT}
                            xTickFormatter={xAxisFormatters[timeScale.value]}
                            tooltipLabelFormatter={tooltipFormatters[timeScale.value]}
                            loading={loading || gamesLoading}
                            keysMap={keysMap}
                            emptyDataKeys={emptyDataKeys}
                        />
                        <ChartOverview>
                            <ChartOverview.Item
                                loading={gamesLoading || peak.all.loading}
                                label={
                                    <SummaryItemLabel
                                        tooltip={getMessage(CODES.tooltipPeakAllTime)}>
                                        {getMessage(CODES.peakAllTime)}
                                    </SummaryItemLabel>
                                }>
                                {formatNumber(peak.all.amount)}
                            </ChartOverview.Item>
                            <ChartOverview.Item
                                loading={gamesLoading || peak.today.loading}
                                label={
                                    <SummaryItemLabel tooltip={getMessage(CODES.tooltipPeakToday)}>
                                        {getMessage(CODES.peakToday)}
                                    </SummaryItemLabel>
                                }>
                                {formatNumber(peak.today.amount)}
                                <Trend value={peak.today.trend} />
                            </ChartOverview.Item>
                            <ChartOverview.Item
                                loading={gamesLoading || avg.all.loading}
                                label={
                                    <SummaryItemLabel tooltip={getMessage(CODES.tooltipAvgAllTime)}>
                                        {getMessage(CODES.avgAllTime)}
                                    </SummaryItemLabel>
                                }>
                                {formatNumber(avg.all.amount)}
                            </ChartOverview.Item>
                            <ChartOverview.Item
                                loading={gamesLoading || avg.today.loading}
                                label={
                                    <SummaryItemLabel tooltip={getMessage(CODES.tooltipAvgToday)}>
                                        {getMessage(CODES.avgToday)}
                                    </SummaryItemLabel>
                                }>
                                {formatNumber(avg.today.amount)}
                                <Trend value={avg.today.trend} />
                            </ChartOverview.Item>
                        </ChartOverview>
                    </ChartWrapper>
                </Widget>
            </div>
        </PDFPictureTrap>
    );
};

export default TotalActiveUsersWidget;
